import React from "react";
import styled from "styled-components";
import { withPrefix, Link } from "gatsby";
import FormattedDate from "../FormattedDate";

import cleanCompanyName from "../../utilities/cleanCompanyName";
import VideoPlayer from "../VideoPlayer";
import MarkMatchedText from "../MarkMatchedText";
import Permalink from "../Permalink";

import ReactMarkdown from "react-markdown";


const ImpactItem = ({item, searchTerm = "", showEntities = true, showTag = true, permalink = undefined}) => {

  return (
    <StyledImpactItem>
      {showTag && item.type && (
        <span>{item.type}</span>
      )}
      {permalink && (
        <div className="permalink">
          <Permalink href={permalink} />
        </div>
      )}
      {item.slug && (
        <div className="anchor" id={item.slug} />
      )}
      <h2>
        <MarkMatchedText text={item.impact} searchText={searchTerm} />
      </h2>
      {item.video && (
        <VideoPlayer
          src={item.video.src}
          poster={item?.video?.poster || undefined}
        />
      )}
      <div className="meta">
        {item?.sources?.length > 0 && (
          <cite>
            <span>Source{item.sources.length > 1 && "s"}:{" "}</span>
            {item.sources.map((source, index) => (
              <React.Fragment key={`${item.slug}-source-${index}`}>
                <a href={source?.use === "pdf" ? source.pdf : source.url} title={source.name} target="_blank" rel="noopener noreferrer" key={index}>{source.name}</a>
                {index < item.sources.length - 1 && ", "}
              </React.Fragment>
            ))}
          </cite>
        )}
        {item.slug ? (
          <a className="date" href={`#${item.slug}`}>
            <FormattedDate date={item.date} />
          </a>
        ) : (
          <span className="date">
            <FormattedDate date={item.date} />
          </span>
        )}
      </div>
      {showEntities && item?.entities?.length > 0 && (
        <div>
          <p><span>Read our discussions with{" "}</span>
            {item.entities.map((entity, index) => (
              <React.Fragment key={`${item.slug}-${entity.slug}`} >
                <a target="_blank" rel="noopener noreferrer" key={index} href={withPrefix(`/discussion/#${entity.slug}`)} title={entity.name}>{cleanCompanyName(entity.name)}</a>{index < item.entities.length - 1 && <span>, </span>}
              </React.Fragment>
            ))}<span>.</span>
          </p>
        </div>
      )}
    </StyledImpactItem>
  );
}

export default ImpactItem;

const StyledImpactItem = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5em;
  padding: 1.5em;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 1em;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: rgba(0,0,0,0.7);
    border-radius: inherit;
    z-index: -1;
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
  }
  > span {
    font-size: 0.875em;
    font-weight: 700;
    letter-spacing: 0.01em;
    background-color: var(--color-white);
    color: var(--color-black);
    justify-self: start;
    padding: 0.5em 0.5em 0.125em;
    margin-top: -1.5rem;
    align-self: start;
  }

  .permalink {
    position: absolute;
    top: 0.75em;
    right: 0.75em;
  }

  .anchor {
    position: absolute;
    top: 0;
    left: 0;
    margin-top: calc(-1 * (var(--navbar-height) + 6em));
  }

  .meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75em;
    @media ( max-width: 48em ) {
      flex-direction: column;
      align-items: start;
    }
  }
  .date {
    font-size: 0.75em;
    font-weight: 700;
    letter-spacing: 0.01em;
    opacity: 0.5;
    @media ( min-width: 48em ) {
      font-size: 0.75em;
    }
  }
  h2 {
    font-size: 0.9375em;
    margin: 0;
    @media ( min-width: 48em ) {
      font-size: 1.125em;
    }

    a {
      text-decoration-line: underline;
      text-decoration-thickness: 0.75px;
      text-underline-offset: 0.1em;
      text-decoration-color: var(--theme-heavy-border-against-dark);
      transition: opacity 0.2s ease;
      opacity: 0.7;
      .can-hover &:hover,
      &:active {
        opacity: 1;
      }
    }
  }
  p {
    margin: 0;
    opacity: 0.7;
    font-size: 0.875em;
  }
  cite {
    position: relative;
    font-style: normal;
    font-size: 0.875em;
    font-weight: 700;
    padding: 0.5em 1em;
    justify-self: start;
    border-radius: 0.5em;
    contain: paint;
    &::before {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: currentColor;
      opacity: 0.1;
      z-index: -1;
    }
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: 0.25em;
      background-color: currentColor;
    }
    > span {
      opacity: 0.5;
    }
    > a {
      text-decoration-line: underline;
      text-decoration-thickness: 0.75px;
      text-underline-offset: 0.1em;
      text-decoration-color: var(--theme-heavy-border-against-dark);
      transition: opacity 0.2s ease;
      opacity: 1;
    }
    .can-hover & > a:hover,
    > a:active {
      opacity: 0.5;
    }
  }
  @media ( min-width: 48em ) {
    p {
      font-size: 0.9375em;
    }
  }
  > div {
    position: relative;
    /*&::before {
      content: "";
      position: absolute;
      top: 0.25em;
      left: 0;
      width: 0.125em;
      bottom: 0.2em;
      background-color: currentColor;
      z-index: -1;
    }*/
    h3 {
      font-weight: 400;
      font-size: 0.875em;
      opacity: 0.7;
      margin: 0;
    }
    p {
      span {
        opacity: 0.7;
      }
      > a {
        text-decoration-line: underline;
        text-decoration-thickness: 0.75px;
        text-underline-offset: 0.1em;
        text-decoration-color: var(--theme-heavy-border-against-dark);
        transition: opacity 0.2s ease;
        opacity: 1;
      }
      .can-hover & > a:hover,
      > a:active {
        opacity: 0.5;
      }
    }
  }
`;